/**
 * The Home's Program card while the can-do path is on (plan 46, P5): the same
 * card as `ProgramHeroCard` (badge, title, one row, footer), with the row fed
 * by `GET /can-do/next` — "Seguí: <lesson or final scenario> · <the can-do>".
 * The row opens that unit's panel in `/program`, like the old card opened its
 * unit. The data is part of the Home's hold-and-reveal gate (`useCanDoNext`
 * resolved), so the card never grows after the reveal.
 *
 * With the flag off the Home renders `ProgramHeroCard` exactly as before; an
 * error here is an error row with a retry, never the old card.
 */
import type { CanDoLoadState } from '../../program/can-do/useCanDo.js';
import type { CanDoLevel, CanDoNextStepView } from '../../../lib/canDoApi.js';
import { CAN_DO_COPY, loc } from '../../program/can-do/canDoCopy.js';
import { IconArrowRight, IconBook, IconChevronRight } from '../icons.js';

const CORAL = '#ff9d6c';

type NextState = CanDoLoadState<{ learnerLevel: CanDoLevel; next: CanDoNextStepView }>;

interface Props {
  state: NextState;
  /** Opens `/program`, on a unit's panel when given. */
  onOpen: (unitId: string | null) => void;
  onExplore: () => void;
}

export function CanDoHeroCard({ state, onOpen, onExplore }: Props) {
  const copy = CAN_DO_COPY.es;
  const next = state.status === 'on' ? state.data.next : null;
  const unitId = next?.kind === 'step' ? next.unitId : null;
  const failed = state.status === 'error';
  const activate = (): void => (failed ? state.reload() : onOpen(unitId));

  let tag: string | null = null;
  let title = '';
  let meta = '';
  if (failed) {
    tag = copy.homeErrorTag;
    title = copy.homeErrorTitle;
    meta = copy.homeErrorMeta;
  } else if (next?.kind === 'step') {
    tag = next.step.kind === 'capstone' ? copy.stepKind.capstone : copy.stepKind.lesson;
    title = loc(next.title);
    meta = loc(next.canDo);
  } else if (next?.kind === 'level_complete') {
    title = copy.homeLevelComplete(next.level);
    meta = copy.homeLevelCompleteMeta;
  } else if (next?.kind === 'no_units') {
    title = copy.homeNoUnitsTitle;
    meta = copy.homeNoUnitsMeta(next.level);
  }

  return (
    <div className="h2-card h2-program">
      <img className="h2-art" src="/home/programa.svg" alt="" aria-hidden="true" />
      <span className="h2-badge" style={{ background: 'rgba(255,157,108,0.15)', color: CORAL }}>
        {copy.homeBadge}
      </span>
      <div className="h2-title" style={{ marginBottom: 7 }}>
        {copy.homeTitle}
      </div>
      <div
        className="h2-inner"
        onClick={activate}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') activate();
        }}
        role="button"
        tabIndex={0}
        aria-label={failed ? `${copy.homeErrorTitle}. ${copy.retry}` : copy.homeOpen}
      >
        <span className="h2-cover" style={{ background: 'rgba(255,157,108,0.16)', color: CORAL }}>
          <IconBook size={19} />
        </span>
        <div className="h2-inner-info">
          {tag && (
            <span className="h2-inner-tag" style={{ color: CORAL }}>
              <span className="dot" style={{ background: CORAL }} />
              {tag}
            </span>
          )}
          <div className="h2-inner-title">{title}</div>
          <div className="h2-inner-meta">{meta}</div>
        </div>
        <span
          className="h2-roundbtn"
          style={{ background: CORAL, color: '#2a1408' }}
          aria-hidden="true"
        >
          {failed ? '↻' : <IconArrowRight size={15} />}
        </span>
      </div>
      <button type="button" className="h2-explore" style={{ color: CORAL }} onClick={onExplore}>
        <span>{copy.homeAll}</span>
        <IconChevronRight size={16} />
      </button>
    </div>
  );
}
